import { XMLParseCallback, getValidStyles } from './common';
import { isExpression } from '../../../code_export/generators/common/expression_export';
import { validateExpressionSyntax } from '../../../code_export/generators/common/expression_validate';
import type { ElementSchema, LVGLSchemaService, PropertyParamSchema } from './LVGLSchemaService';
import { stripXmlCommentsPreserveLines } from './common';
import {
    COMPOUND_COORDINATE_TYPE,
    COMPOUND_COORDINATE_NO_CONTENT_TYPE,
    type IntegerLikeType,
    isIntegerLikeType,
    VALID_C_VAR_NAME,
    VALID_C_VAR_NAME_DESCRIPTION,
    VALID_PROJECT_NAME_DESCRIPTION,
    VALID_PROJECT_NAME_REGEX,
    VALID_IF_TARGET_REGEX,
    VALID_IF_TARGET_DESCRIPTION
} from '../../../services/constants';

type CollectPropertyParamSchemasOptions = {
    requireName?: boolean;
    requireType?: boolean;
    useEmptyStringForMissingHelp?: boolean;
};

type ParentToValidate = { parent?: string; propsAndArgsToValidate: PropInfo[] };
type ValidationContext = ParentToValidate & {
    documentConstNames?: readonly string[];
    /** Resolved screen identifiers from the project registry and the current validation pass. */
    knownScreenNames?: readonly string[];
};

export type PropInfo = {
    fullProp: string;
    propName: string;
    propValue: string;
    lineNumber: number;
    startColumn: number;
    endColumn: number;
};

export type ValidationResult = {
    isValid: boolean;
    message?: string;
};

const isParamElement = (element: Element): boolean => element.tagName.toLowerCase() === 'param';

const hasDirectParamChild = (element: Element): boolean => Array.from(element.children).some(isParamElement);

const propTagHasDirectParamChild = (propTag: string): boolean => {
    if (/\/\s*>$/.test(propTag)) return false;

    const firstTagEndIndex: number = propTag.indexOf('>');
    if (firstTagEndIndex === -1) return false;

    const propContent: string = propTag.substring(firstTagEndIndex + 1);
    const childTagPattern: RegExp = /<\/?([a-zA-Z][\w-]*)(?:\s+[^>]*)?\/?>/g;
    let depth: number = 1;
    let childTagMatch: RegExpExecArray | null;

    while ((childTagMatch = childTagPattern.exec(propContent)) !== null) {
        const childTag: string = childTagMatch[0];
        const childTagName: string = childTagMatch[1].toLowerCase();
        const isClosingTag: boolean = childTag.startsWith('</');
        const isSelfClosingTag: boolean = /\/\s*>$/.test(childTag);

        if (isClosingTag) {
            depth--;
            if (depth === 0) break;
            continue;
        }

        if (childTagName === 'param' && depth === 1) return true;

        if (!isSelfClosingTag) depth++;
    }

    return false;
};

// Validates a prop or const reference, just by checking that
// the prop or const exists and is the right type.
//
// value = value of property
//
// return ValidationResult if invalid, true if valid, or false if not a prop or const
//
export const validatePropOrConstReference = (
    value: string,
    schemaService: LVGLSchemaService,
    documentConstNames: readonly string[] = []
): ValidationResult | undefined => {
    if (value.startsWith('$')) {
        // TODO: Check the props to make sure this reference exists and is the same type. [Architectural Improvement]
        return {
            isValid: true
        };
    } else if (value.startsWith('#')) {
        const constExists: boolean =
            documentConstNames.some((constName: string) => `#${constName}` === value) ||
            Array.from(schemaService.consts.values()).some((constDef) => `#${constDef.name}` === value);

        if (!constExists) {
            return {
                isValid: false,
                message: `Const reference '${value}' not found.`
            };
        }
        return {
            isValid: true
        };
    }
};

export const validateEnum = (
    value: string,
    propName: string,
    baseType: string,
    schemaService: LVGLSchemaService,
    documentConstNames: readonly string[] = []
): ValidationResult | undefined => {
    const validateRef: ValidationResult | undefined = validatePropOrConstReference(
        value,
        schemaService,
        documentConstNames
    );
    if (validateRef) return validateRef;

    // Normally, we need to check if the property has a valid enum value, from the enum
    // corresponding to the type of the property.
    // So if property is of type 'ENUM_MY_COLORS' and those can be 'RED' and 'GREEN', the value
    // must be either 'RED' or 'GREEN'.
    //
    // The special case is for the 'type' property. It may be any type OR it may be the name of
    // an existing enum.
    const enumName: string = baseType.substring(5);
    const validValues: string[] = schemaService.getEnumValues(enumName);

    // Special case, the property is 'type'. It may be an array type, in which case
    // any base type followed by '[...]' (empty or any content, e.g. 'int[]',
    // 'color[count]', 'subject[3]') is allowed; only the base type is validated.
    // It may also reference an enum (e.g. 'enum:my_enum') or an array of one.
    if (propName === 'type') {
        // Strip an optional trailing '+' (OR-able multi-value) and any array '[...]' suffix.
        value = value.replace(/\+$/, '').replace(/\[[^\]]*\]$/, '');

        if (value.startsWith('enum:')) {
            value = value.substring(5);
            validValues.push(...schemaService.enums.keys());
        }
    }

    if (!validValues.includes(value)) {
        return {
            isValid: false,
            message: `Invalid enum value '${value}' for type '${enumName}'. Valid values are: ${validValues.join(', ')}`
        };
    }
};

export const validateColor = (
    value: string,
    schemaService: LVGLSchemaService,
    documentConstNames: readonly string[] = []
): ValidationResult | undefined => {
    const validateRef: ValidationResult | undefined = validatePropOrConstReference(
        value,
        schemaService,
        documentConstNames
    );
    if (validateRef) return validateRef;

    if (!value.match(/^0x([0-9A-Fa-f]{3}|[0-9A-Fa-f]{6})$/)) {
        return {
            isValid: false,
            message: `Invalid color format: '${value}'. Should be 0xRRGGBB, or 0xRGB or a #const or $prop reference`
        };
    }
};

export const validateOpa = (
    value: string,
    schemaService: LVGLSchemaService,
    documentConstNames: readonly string[] = []
): ValidationResult | undefined => {
    const validateRef: ValidationResult | undefined = validatePropOrConstReference(
        value,
        schemaService,
        documentConstNames
    );
    if (validateRef) return validateRef;

    const validFormat: RegExpMatchArray | null = value.match(/^\d+%?$/);
    const numberValue: number = parseInt(value.replace('%', ''));
    const isPercentage: boolean = value.endsWith('%');
    const inRange: boolean = numberValue >= 0 && numberValue <= (isPercentage ? 100 : 255);

    if (!validFormat || !inRange) {
        return {
            isValid: false,
            message: `Invalid opacity value: '${value}'. Should be 0...255 or 0...100% or a #const or $prop reference`
        };
    }
};

export const validateBool = (
    value: string,
    schemaService: LVGLSchemaService,
    documentConstNames: readonly string[] = []
): ValidationResult | undefined => {
    const validateRef: ValidationResult | undefined = validatePropOrConstReference(
        value,
        schemaService,
        documentConstNames
    );
    if (validateRef) return validateRef;

    if (!value.match(/(true)|(false)/)) {
        return {
            isValid: false,
            message: `Invalid boolean value: '${value}'. Should be 'true', 'false' or a #const or $prop reference.`
        };
    }
};

// May be an integer, px, %, certain keywords, or a const/prop reference.
// May not be a subject.
export const validateIntegerLikeValue = (
    value: string,
    type: IntegerLikeType,
    schemaService: LVGLSchemaService,
    documentConstNames: readonly string[] = []
): ValidationResult | undefined => {
    const validateRef: ValidationResult | undefined = validatePropOrConstReference(
        value,
        schemaService,
        documentConstNames
    );
    if (validateRef) return validateRef;

    const isInteger: boolean = /^-?\d+$/.test(value);
    const isPixel: boolean = /^-?\d+(?:px)?$/.test(value);
    const isPercentage: boolean = /^\d+%$/.test(value);
    const isContent: boolean = value === 'content';
    const validByType: Record<IntegerLikeType, boolean> = {
        int: isInteger || isPixel || isPercentage,
        px: isPixel,
        '%': isPercentage,
        percentage: isPercentage,
        content: isContent,
        [COMPOUND_COORDINATE_TYPE]: isPixel || isPercentage || isContent,
        [COMPOUND_COORDINATE_NO_CONTENT_TYPE]: isPixel || isPercentage
    };

    if (validByType[type]) return undefined;

    return {
        isValid: false,
        message: `Invalid ${type} value: '${value}'. Expected a value supported by '${type}', a constant reference (e.g. '#const_ref'), or an API reference (e.g. '$api_ref')`
    };
};

// Validate a grid track descriptor, e.g. "20 30 content" or "1fr fr(2)".
// Each space-separated token is a non-negative integer (px), a fraction unit
// ('1fr' or 'fr(1)'), the 'content' keyword, or a #const / $api reference.
export const validateGridDsc = (
    value: string,
    schemaService: LVGLSchemaService,
    documentConstNames: readonly string[] = []
): ValidationResult | undefined => {
    for (const token of value.trim().split(/\s+/).filter(Boolean)) {
        const validateRef: ValidationResult | undefined = validatePropOrConstReference(
            token,
            schemaService,
            documentConstNames
        );
        if (validateRef) {
            if (!validateRef.isValid) return validateRef;
            continue;
        }

        if (token !== 'content' && !token.match(/^\d+$/) && !token.match(/^\d+fr$/) && !token.match(/^fr\(\d+\)$/)) {
            return {
                isValid: false,
                message: `Invalid grid track size: '${token}'. Should be a number (px), a fraction (e.g. '3fr'), 'content', or a #const/$api reference.`
            };
        }
    }
};

// Validate a precise point list (lv_point_precise_t), e.g. "(0 10) (60 50)".
// Each point must be parenthesized; coordinates may be integers or floats. The array
// form allows one or more points, the scalar form exactly one.
export const validatePrecisePoints = (value: string, isArray: boolean): ValidationResult => {
    const number: string = String.raw`-?\d+(?:\.\d+)?`;
    const point: string = String.raw`\(\s*${number}\s+${number}\s*\)`;

    if (isArray) {
        if (!new RegExp(String.raw`^\s*(?:${point}\s*)+$`).test(value)) {
            return {
                isValid: false,
                message: `Invalid points value: '${value}'. Use one or more parenthesized points, e.g. '(10 20) (30 40)'. Coordinates may be integers or floats.`
            };
        }
        return { isValid: true };
    }

    if (!new RegExp(String.raw`^\s*${point}\s*$`).test(value)) {
        return {
            isValid: false,
            message: `Invalid point value: '${value}'. Use a parenthesized point, e.g. '(10 20)'. Coordinates may be integers or floats.`
        };
    }
    return { isValid: true };
};

// Collect API prop names that are valid enough to be referenced by $prop.
// Validation errors for malformed <api><prop> definitions are emitted by XMLValidatorCore.
export const collectDocumentApiProps = (document: Document): string[] =>
    Array.from(document.querySelectorAll('api > prop'))
        .map((prop: Element): string | null => {
            const name: string | null = prop.getAttribute('name');
            const type: string | null = prop.getAttribute('type');
            const hasParams: boolean = hasDirectParamChild(prop);

            if (!name) return null;
            if ((type && type.trim().length > 0) || hasParams) return name;

            return null;
        })
        .filter((name: string | null): name is string => !!name);

// Collect const names from the document being validated so same-file references
// are valid even while the schema service is reloading cached globals on save.
export const collectDocumentConsts = (document: Document): string[] =>
    Array.from(document.querySelectorAll('consts > *'))
        .map((constant: Element): string | null => constant.getAttribute('name'))
        .filter((name: string | null): name is string => !!name);

// Collect local API prop names from <api><prop .../> anywhere in the document
export const collectDocumentApiPropsFromDocument = (docText: string, parseXml?: XMLParseCallback): string[] => {
    if (parseXml) return collectDocumentApiProps(parseXml(docText));

    // Fallback for callers that only have text available, such as completion.
    const apiBlockMatch: RegExpMatchArray | null = docText.match(/<api\b[^>]*>([\s\S]*?)<\/api>/i);
    if (!apiBlockMatch) return [];

    const apiInner: string = apiBlockMatch[1];
    const results: string[] = [];
    const propTagRe: RegExp = /<prop\b[\s\S]*?(?:\/>|<\/prop>)/gi;
    const attrRe: RegExp = /([\w:-]+)\s*=\s*["']([^"']*)["']/g;

    let m: RegExpExecArray | null;
    while ((m = propTagRe.exec(apiInner)) !== null) {
        const tag: string = m[0];
        const openingTag: string = tag.match(/^<prop\b[^>]*>/i)?.[0] ?? '';
        let name: string | undefined;
        let type: string | undefined;
        const hasParams: boolean = propTagHasDirectParamChild(tag);

        let attr: RegExpExecArray | null;
        while ((attr = attrRe.exec(openingTag)) !== null) {
            const key: string = attr[1];
            const value: string = attr[2];
            if (key === 'name') name = value;
            if (key === 'type') type = value;
        }
        if (name && ((type && type.trim().length > 0) || hasParams)) results.push(name);
    }

    return results;
};

export type ViewWidgetEntry = { name: string; type: string };

// Schema types that may appear as named, animatable UI elements inside <view>
const VIEW_WIDGET_SCHEMA_TYPES: ReadonlySet<string> = new Set<string>(['lvgl-widget', 'widget', 'component']);

const ANIMATION_TARGET_TYPES: ReadonlySet<string> = new Set<string>(['lv_obj', 'lv_obj|self']);

// Collect named widgets/components inside <view>...</view> from raw XML text.
// Tags known to the schema as non-widgets (e.g. <style name="...">) are skipped;
// unknown tags are kept so unloaded components do not cause false negatives.
export const collectViewWidgetsFromDocument = (
    docText: string,
    schemaService?: LVGLSchemaService
): ViewWidgetEntry[] => {
    const viewMatch: RegExpMatchArray | null = docText.match(/<view\b[^>]*>([\s\S]*?)<\/view>/);
    if (!viewMatch) return [];

    const entries: ViewWidgetEntry[] = [];
    const tagPattern: RegExp = /<([a-zA-Z][\w-]*)((?:\s+[^<>]*)?)\/?>/g;
    let tagMatch: RegExpExecArray | null;

    while ((tagMatch = tagPattern.exec(viewMatch[1])) !== null) {
        const [, tagName, attributes] = tagMatch;
        const name: string | undefined = attributes.match(/\bname\s*=\s*["']([^"']*)["']/)?.[1]?.trim();
        if (!name) continue;

        const schema: ElementSchema | undefined = schemaService?.getElement(tagName);
        if (schema && !VIEW_WIDGET_SCHEMA_TYPES.has(schema.type)) continue;

        entries.push({ name, type: tagName });
    }

    return entries;
};

// Collect timeline names from the <animations> section of raw XML text
// (used for target="self", so unsaved edits in the current document count).
export const collectDocumentTimelineNames = (docText: string): string[] => {
    const animationsMatch: RegExpMatchArray | null = docText.match(/<animations\b[^>]*>([\s\S]*?)<\/animations>/);
    if (!animationsMatch) return [];

    const names: Set<string> = new Set<string>();
    const timelinePattern: RegExp = /<timeline\b((?:\s+[^<>]*)?)\/?>/g;
    let timelineMatch: RegExpExecArray | null;

    while ((timelineMatch = timelinePattern.exec(animationsMatch[1])) !== null) {
        const name: string | undefined = timelineMatch[1].match(/\bname\s*=\s*["']([^"']*)["']/)?.[1]?.trim();
        if (name) names.add(name);
    }

    return Array.from(names);
};

/** Valid values for a UI element reference: named view widgets, plus 'self' for 'lv_obj|self'. */
export const getAnimationTargetNames = (
    docText: string,
    targetType: string,
    schemaService?: LVGLSchemaService
): string[] =>
    Array.from(
        new Set([
            ...(targetType === 'lv_obj|self' ? ['self'] : []),
            ...collectViewWidgetsFromDocument(docText, schemaService).map((widget) => widget.name)
        ])
    );

/**
 * Timeline names available on a given animation target.
 * - 'self' (or empty): timelines defined in the current document.
 * - A named view widget: timelines from the widget type's loaded schema.
 * Returns undefined when the timelines cannot be determined (unknown target or unloaded
 * type), in which case validation should be skipped.
 */
export const getTimelineNamesForTarget = (
    docText: string,
    target: string | undefined,
    schemaService: LVGLSchemaService
): string[] | undefined => {
    if (!target || target === 'self') return collectDocumentTimelineNames(docText);

    const widget: ViewWidgetEntry | undefined = collectViewWidgetsFromDocument(docText, schemaService).find(
        (entry) => entry.name === target
    );
    if (!widget) return undefined;

    return schemaService.getElement(widget.type)?.timelines;
};

export type PrimitiveType = 'int' | 'float' | 'string';

// Validate that a string can be converted to the requested type
export const canCoerceToGiveType = (type: PrimitiveType, input: string): boolean => {
    const string: string = input.trim();
    switch (type) {
        case 'int':
            return /^[+-]?\d+$/.test(string) && Number.isSafeInteger(Number(string));
        case 'float':
            if (
                /^[+-]?(?:\d+\.\d*|\.\d+|\d+\.)(?:[eE][+-]?\d+)?$/.test(string) ||
                /^[+-]?\d+(?:[eE][+-]?\d+)$/.test(string)
            ) {
                return Number.isFinite(Number(string));
            }
            return false;
        case 'string':
            return true;
    }
};

const ifNull = <T>(v: T | null, fallback: any): T | any => (v === null ? fallback : v);

export const collectPropertyParamSchemas = (
    prop: Element,
    {
        requireName = false,
        requireType = false,
        useEmptyStringForMissingHelp = false
    }: CollectPropertyParamSchemasOptions = {}
): PropertyParamSchema[] =>
    Array.from(prop.children)
        .filter(isParamElement)
        .flatMap((param: Element) => {
            const name: string | null = param.getAttribute('name');
            const type: string | null = param.getAttribute('type');

            if ((requireName && !name) || (requireType && !type)) return [];

            return [
                {
                    name: name || '',
                    type: type || '',
                    help: useEmptyStringForMissingHelp
                        ? param.getAttribute('help') || ''
                        : ifNull(param.getAttribute('help'), undefined),
                    default: ifNull(param.getAttribute('default'), undefined),
                    isParam: true
                }
            ];
        });

export const propHasDirectParamChild = (docText: string, tagStartIndex: number, fullMatch: string): boolean => {
    if (/\/\s*>$/.test(fullMatch) || tagStartIndex < 0) return false;

    const propContent: string = docText.substring(tagStartIndex + fullMatch.length);
    const childTagPattern: RegExp = /<param\b[^>]*>|<prop\b[^>]*\/?>|<\/prop>/gi;
    let propDepth: number = 1;
    let childTagMatch: RegExpExecArray | null;

    while ((childTagMatch = childTagPattern.exec(propContent)) !== null) {
        const childTag: string = childTagMatch[0];

        if (/^<param\b/i.test(childTag) && propDepth === 1) return true;

        if (/^<prop\b/i.test(childTag)) {
            if (!/\/\s*>$/.test(childTag)) propDepth++;
            continue;
        }

        if (/^<\/prop>/i.test(childTag)) {
            propDepth--;
            if (propDepth === 0) break;
        }
    }

    return false;
};

export const isValidScreenName = (value: string): boolean => {
    if (!value) return false;
    const parts: string[] = value.split('/').filter(Boolean);
    if (!parts.length) return false;
    return parts.every((part) => VALID_C_VAR_NAME.test(part));
};

/** Read the `name` attribute from a `<screen>` root element in raw XML (comments stripped). */
export const parseScreenNameAttributeFromXml = (xml: string): string | undefined => {
    const withoutComments: string = stripXmlCommentsPreserveLines(xml);
    const trimmed: string = withoutComments.trimStart();
    if (!trimmed.startsWith('<screen')) return undefined;

    const openTagMatch: RegExpMatchArray | null = trimmed.match(/^<screen\b([\s\S]*?)>/);
    if (!openTagMatch) return undefined;

    const nameMatch: RegExpMatchArray | null = openTagMatch[1].match(/\bname\s*=\s*(['"])([^'"]*)\1/);
    if (!nameMatch) return undefined;

    const value: string = nameMatch[2].trim();
    return value || undefined;
};

/**
 * Resolve the screen identifier used by LVGL and screen_load_event references.
 * - With a non-empty `name` attribute: the attribute value is the identifier (may be path-like).
 * - Without `name`: the XML file base name (no directory path) is the identifier.
 */
export const resolveScreenIdentifier = (filePath: string, nameAttribute?: string | null): string => {
    const explicitName: string | undefined = nameAttribute?.trim();
    if (explicitName) return explicitName;
    return filePath.replace(/^.*[\\/]/, '').replace(/\.xml$/i, '');
};

/** @deprecated Prefer {@link resolveScreenIdentifier} with a null name attribute. */
export const getDefaultScreenNameFromPath = (_filePath: string, fileBaseName: string): string => fileBaseName;

const getSubjectsOfType = (schemaService: LVGLSchemaService, type?: string): string[] => {
    if (!type) return schemaService.subjects;

    // Normalize type markers that are not part of the core type name:
    // - Strip array notation "[]" (e.g. "int[]" -> "int", "string[][]" -> "string")
    // - Strip optional suffix "+" (e.g. "color+" -> "color")
    const normalizedType: string = type.replace(/\[\]/g, '').replace(/\+$/, '');
    const mappedSubjectType: string | undefined = (() => {
        if (normalizedType === 'string' || normalizedType.startsWith('string:')) return 'string';
        if (isIntegerLikeType(normalizedType) || normalizedType === 'deg' || normalizedType === 'opa') return 'int';
        if (normalizedType === 'float') return 'float';
        if (normalizedType === 'color') return 'color';
        if (normalizedType === 'pointer') return 'pointer';
        return undefined;
    })();

    if (!mappedSubjectType) return schemaService.subjects;

    const typedSubjects: Set<string> | undefined = schemaService.subjectsByType[mappedSubjectType];
    return typedSubjects?.size ? Array.from(typedSubjects) : [];
};

export const getCompatibleSubjectSuggestions = (
    schemaService: LVGLSchemaService,
    propName: string,
    propType: string | undefined,
    propsAndArgsToValidate: PropInfo[]
): string[] => {
    if (propName === 'value') {
        const targetSubjectName: string | undefined = propsAndArgsToValidate.find(
            ({ propName }) => propName === 'name' || propName === 'subject'
        )?.propValue;
        if (targetSubjectName) {
            const targetSubjectType: string | undefined = Object.entries(schemaService.subjectsByType).find(
                ([, subjectNames]) => subjectNames.has(targetSubjectName)
            )?.[0];
            const matchingSubjects: string[] = getSubjectsOfType(schemaService, targetSubjectType);
            if (matchingSubjects?.length)
                // 1) For `value`, if an existing matching subject name is found, return only its typed subjects
                return matchingSubjects;
        }
    }

    const compatibleSubjects: string[] = getSubjectsOfType(schemaService, propType);
    // 2) If property type is known, return that type's subjects
    // 3) Otherwise, return full subject list as fallback
    return compatibleSubjects.length ? compatibleSubjects : schemaService.subjects;
};

// Helper function to validate property value based on type (schema = schema of the component/widget)
export const validatePropertyValue = (
    docText: string,
    propName: string,
    value: string,
    type: string,
    schemaService: LVGLSchemaService,
    elementName: string,
    isRoot: boolean,
    { parent, propsAndArgsToValidate, documentConstNames = [], knownScreenNames }: ValidationContext
): ValidationResult => {
    if (!type) return { isValid: true };

    // Expression values (`{ ... }`) opt into expression evaluation. Validate the
    // expression *grammar* here (a TS port of the runtime evaluator's parser);
    // types and identifier resolution are still checked at runtime by the C
    // evaluator. See validateExpressionSyntax in
    // code_export/generators/common/expression_validate.ts.
    if (isExpression(value)) return validateExpressionSyntax(value);

    // Precise point lists need parens-aware validation, so handle before generic array splitting.
    if (type.replace(/\[[^\]]*\]/g, '') === 'precise_points') {
        return validatePrecisePoints(value, type.includes('['));
    }

    // Handle multi-value types (which can be OR'ed together) (ending with +)
    const isMultiValue: boolean = type.endsWith('+');
    // Array types are any base type followed by '[...]' (empty or any content,
    // e.g. 'int[]', 'color[count]', 'subject[3]'); each space-separated element
    // is validated against the base type.
    const isArrayValue: boolean = /\[[^\]]*\]/.test(type);
    const baseType: string = isMultiValue ? type.slice(0, -1) : type;

    // For multi-value types, split by | and validate each value
    // NOTE: It may also be a multi-value array type: []+
    // Meaning it may have different values separated by spaces
    // and OR'ed together.
    if (isMultiValue || isArrayValue) {
        let values: string[] = [];
        if (isArrayValue)
            values = value.split(/[| ]/); // Split by | and ' '
        else values = value.split('|'); // Just split by | as it is not an array value

        // Fixed-size array types (e.g. 'image[4]') must have exactly that many elements.
        if (isArrayValue) {
            const fixedSizeMatch: RegExpMatchArray | null = type.match(/\[(\d+)\]$/);
            if (fixedSizeMatch) {
                const expected: number = parseInt(fixedSizeMatch[1], 10);
                const actual: number = values.filter((val: string) => val.trim().length > 0).length;
                if (actual !== expected) {
                    return {
                        isValid: false,
                        message: `'${type}' requires exactly ${expected} value${
                            expected === 1 ? '' : 's'
                        }, but got ${actual}.`
                    };
                }
            }
        }

        for (const val of values) {
            const result: ValidationResult = validatePropertyValue(
                docText,
                propName,
                val,
                baseType.replace(/\[[^\]]*\]/, ''),
                schemaService,
                elementName,
                isRoot,
                {
                    parent,
                    propsAndArgsToValidate,
                    documentConstNames
                }
            );

            if (!result.isValid) return result;
        }

        return { isValid: true };
    }

    // Validate enum values
    else if (baseType.startsWith('enum:')) {
        const result: ValidationResult | undefined = validateEnum(
            value,
            propName,
            baseType,
            schemaService,
            documentConstNames
        );
        if (result) return result;
    }

    // Validate LVGL event codes (e.g. clicked, pressed)
    if (baseType === 'lv_event') {
        const refCheck: ValidationResult | undefined = validatePropOrConstReference(
            value,
            schemaService,
            documentConstNames
        );
        if (refCheck) return refCheck;

        const validEvents: string[] = schemaService.getEnumValues('lv_event');
        if (!validEvents.includes(value)) {
            return {
                isValid: false,
                message: `Invalid lv_event value '${value}'. Valid values are: ${validEvents.join(', ')}`
            };
        }
    }

    // Validate event callback function names
    else if (baseType === 'event_cb') {
        const refCheck: ValidationResult | undefined = validatePropOrConstReference(
            value,
            schemaService,
            documentConstNames
        );
        if (refCheck) return refCheck;

        if (!VALID_C_VAR_NAME.test(value)) {
            return {
                isValid: false,
                message: `Invalid event callback '${value}'. ${VALID_C_VAR_NAME_DESCRIPTION}`
            };
        }
    }

    // Validate screen references (path-like names, e.g. main_menu or folder/screen1)
    else if (baseType === 'screen') {
        const refCheck: ValidationResult | undefined = validatePropOrConstReference(
            value,
            schemaService,
            documentConstNames
        );
        if (refCheck) return refCheck;

        if (!isValidScreenName(value)) {
            return {
                isValid: false,
                message: `Invalid screen '${value}'. Use path-like names (e.g. 'main_menu' or 'folder/screen1') with valid identifier segments.`
            };
        }

        const knownScreens: readonly string[] = knownScreenNames ?? schemaService.getScreenNames();
        if (knownScreens.length > 0 && !knownScreens.includes(value)) {
            return {
                isValid: false,
                message: `Unknown screen '${value}'. Known screens: ${knownScreens.join(', ')}`
            };
        }
    }

    // Validate UI element references: a named widget in <view>, or 'self' when the
    // type allows it (animation/include_timeline/play_timeline_event targets).
    // Plain 'lv_obj' references (e.g. lv_keyboard's textarea) do not accept 'self'.
    else if (ANIMATION_TARGET_TYPES.has(baseType)) {
        const refCheck: ValidationResult | undefined = validatePropOrConstReference(
            value,
            schemaService,
            documentConstNames
        );
        if (refCheck) return refCheck;

        const allowSelf: boolean = baseType === 'lv_obj|self';

        // Path-like names (e.g. 'cont/label') are resolved hierarchically by LVGL; skip those.
        if (!(allowSelf && value === 'self') && !value.includes('/')) {
            const widgetNames: string[] = collectViewWidgetsFromDocument(docText, schemaService).map(
                (widget) => widget.name
            );
            if (!widgetNames.includes(value)) {
                return {
                    isValid: false,
                    message: `Unknown UI element '${value}'. Should be ${
                        allowSelf ? "'self' or " : ''
                    }the name of an element in <view>${widgetNames.length ? `: ${widgetNames.join(', ')}` : ''}`
                };
            }
        }
    }

    // Validate timeline references against the timelines of the element's target
    else if (baseType === 'timeline') {
        const refCheck: ValidationResult | undefined = validatePropOrConstReference(
            value,
            schemaService,
            documentConstNames
        );
        if (refCheck) return refCheck;

        const target: string | undefined = propsAndArgsToValidate.find(
            ({ propName }) => propName === 'target'
        )?.propValue;

        // Skip when the target is a reference or path; an invalid target is reported separately.
        if (!target?.startsWith('$') && !target?.startsWith('#') && !target?.includes('/')) {
            const timelineNames: string[] | undefined = getTimelineNamesForTarget(docText, target, schemaService);
            if (timelineNames !== undefined && !timelineNames.includes(value)) {
                return {
                    isValid: false,
                    message: `Unknown timeline '${value}' for target '${target || 'self'}'.${
                        timelineNames.length
                            ? ` Available timelines: ${timelineNames.join(', ')}`
                            : ' The target defines no timelines.'
                    }`
                };
            }
        }
    }

    // Validate subject values
    else if (baseType === 'subject') {
        const validSubjects: string[] = getCompatibleSubjectSuggestions(
            schemaService,
            propName,
            type,
            propsAndArgsToValidate
        );
        if (!value.startsWith('$') && !validSubjects.includes(value)) {
            return {
                isValid: false,
                message: `Invalid subject '${value}', valid subjects are: ${validSubjects.join(', ')}`
            };
        }
    }

    // Validate color values
    else if (baseType === 'color') {
        const result: ValidationResult | undefined = validateColor(value, schemaService, documentConstNames);
        if (result) return result;
    }

    // Validate integer-like values, including the explicitly supported compound coordinate type.
    else if (isIntegerLikeType(baseType)) {
        const result: ValidationResult | undefined = validateIntegerLikeValue(
            value,
            baseType,
            schemaService,
            documentConstNames
        );
        if (result) return result;
    } else if (baseType.startsWith('grid_dsc')) {
        const result: ValidationResult | undefined = validateGridDsc(value, schemaService, documentConstNames);
        if (result) return result;
    } else if (baseType === 'int:non-negative') {
        if (!value.match(/^(\d+)$/)) {
            return {
                isValid: false,
                message: `Invalid integer value: '${value}'. Should be a non-negative integer.`
            };
        }
    } else if (baseType === 'deg') {
        if (
            // Positive or negative integer
            !value.match(/^-?\d+$/) &&
            // Constant or API reference
            !value.match(/^[#$]?[a-zA-Z_][a-zA-Z0-9_]*$/)
        ) {
            return {
                isValid: false,
                message: `Invalid ${baseType} value: '${value}'. Should be an integer, constant reference (e.g. '#const_ref'), or api reference (e.g. '$api_ref')`
            };
        }
    } else if (baseType === 'opa') {
        // Must be either 0...255 or 0...100%
        const result: ValidationResult | undefined = validateOpa(value, schemaService, documentConstNames);
        if (result) return result;
    } else if (baseType === 'bool') {
        const result: ValidationResult | undefined = validateBool(value, schemaService, documentConstNames);
        if (result) return result;
    } else if (baseType === 'string') {
        const result: ValidationResult | undefined = validatePropOrConstReference(
            value,
            schemaService,
            documentConstNames
        );
        if (result) return result;
    } else if (baseType === 'style' && parent !== 'styles') {
        // For validating references to styles, not where styles are defined
        const refCheck: ValidationResult | undefined = validatePropOrConstReference(
            value,
            schemaService,
            documentConstNames
        );
        if (refCheck) return refCheck;

        const validStyleNames: string[] = getValidStyles(docText, schemaService);
        if (!validStyleNames.includes(value)) {
            return {
                isValid: false,
                message: `Invalid style value: '${value}'. Should be: ${validStyleNames.join(', ')}`
            };
        }
    } else if (baseType === 'string:translation') {
        // Get the char_count so we can validate the length of the translation
        const charCount: number = Number(
            propsAndArgsToValidate.find(({ propName }) => propName === 'char_count')?.propValue
        );
        if (value.length > charCount) {
            return {
                isValid: false,
                message: `'${value}'. 'char_count' is ${charCount} but '${value}' is ${value.length} characters.`
            };
        }
    } else if (baseType === 'string:project_name') {
        if (!VALID_PROJECT_NAME_REGEX.test(value)) {
            return {
                isValid: false,
                message: `Invalid project name: '${value}'. ${VALID_PROJECT_NAME_DESCRIPTION}`
            };
        }
    } else if (baseType === 'string:lvgl_version') {
        const schemaServiceClass = schemaService.constructor as unknown as { LVGL_SCHEMA_VERSIONS: string[] };
        if (!schemaServiceClass.LVGL_SCHEMA_VERSIONS.some((version) => value === version.replace('v', ''))) {
            return {
                isValid: false,
                message: `Invalid LVGL version: '${value}'. Must be one of: ${schemaServiceClass.LVGL_SCHEMA_VERSIONS.map((v) => v.replace('v', '')).join(', ')}.`
            };
        }
    } else if (baseType.startsWith('if_target')) {
        const validTargets: string[] = [...schemaService.getProjectTargetNames()];

        if (baseType === 'if_target:use_target' && value.indexOf('|') > 0) {
            // use_target can not be OR'ed
            return {
                isValid: false,
                message: `'${value}'. 'use_target' may only specify one target and may not be OR'ed`
            };
        } else if (!VALID_IF_TARGET_REGEX.test(value)) {
            return {
                isValid: false,
                message: `Invalid format: '${value}'. ${VALID_IF_TARGET_DESCRIPTION}`
            };
        } else if (value.split('|').some((target) => !validTargets.includes(target))) {
            // It's in the valid format - check if it specifies valid targets
            return {
                isValid: false,
                message: `'${value}'. Valid targets are ${validTargets.join(', ')}`
            };
        }
    } else if (baseType === 'animation:repeat_count') {
        if (!value.match(/^(?:[1-9]\d*|infinite)$/)) {
            return {
                isValid: false,
                message: `Invalid animation:repeat_count value: '${value}'. Should be a non-zero integer or 'infinite'.`
            };
        }
    } else if (baseType === 'style_prop') {
        // Intentionally not using schemaService.getElement because that will lookup the object add style
        const styleProps = schemaService.elements.get('style');
        const allowedStyleProps: string[] = (styleProps?.properties || []).map((styleProp) => styleProp.name);

        if (!allowedStyleProps.includes(value)) {
            return {
                isValid: false,
                message: `Invalid style prop: '${value}'. Must be one of the lv_obj style properties: ${allowedStyleProps.join(', ')}`
            };
        }
    }

    if (baseType === 'image' && parent === 'steps' && propName === 'path' && !value.endsWith('.png')) {
        return {
            isValid: false,
            message: `Invalid screenshot path: '${value}'. Screenshot paths must end with '.png' extension.`
        };
    }

    if (propName === 'name') {
        if (elementName === 'screen' && isRoot) {
            if (!isValidScreenName(value)) {
                return {
                    isValid: false,
                    message: `Invalid screen 'name' value: '${value}'. Use path-like names (e.g. 'new/screen1') with valid identifier segments.`
                };
            }
        } else if (!VALID_C_VAR_NAME.test(value)) {
            return {
                isValid: false,
                message: `Invalid 'name' value: '${value}'. ${VALID_C_VAR_NAME_DESCRIPTION}`
            };
        }
    }

    if (propName === 'extends') {
        if (!value || !schemaService.elements.has(value)) {
            return {
                isValid: false,
                message: `Invalid 'extends' value: '${value}'. Must be a non-empty name of an existing element.`
            };
        }
    }

    return { isValid: true };
};

export const isAllowedChildElement = (elementName: string, allowedChildren: readonly string[]): boolean =>
    allowedChildren.includes(elementName) || allowedChildren.includes(`lv_obj-${elementName}`);

export type OrderValidationResult = ValidationResult & { groupIndex?: number };

/** Index of the group `elementName` belongs to, or undefined if it is not part of `order`. */
export const getElementOrderGroupIndex = (elementName: string, order: string[][]): number | undefined => {
    const groupIndex: number = order.findIndex((group: string[]) => group.includes(elementName));
    return groupIndex === -1 ? undefined : groupIndex;
};

/**
 * All group indices `elementName` belongs to. An element listed in more than one group floats
 * freely relative to those groups (e.g. an element in groups 0 and 1 may appear before or after
 * group 1's other members), while still being subject to the overall group order.
 */
const getElementOrderGroupIndices = (elementName: string, order: string[][]): number[] =>
    order.reduce((indices: number[], group: string[], index: number) => {
        if (group.includes(elementName)) indices.push(index);
        return indices;
    }, [] as number[]);

/**
 * Validates that a child element does not appear before an earlier-ordered sibling group.
 *
 * `maxSeenGroupIndex` is the highest group index already encountered among this element's
 * preceding siblings (undefined if none have belonged to an ordered group yet). Returns
 * undefined when `order` is not defined or `elementName` is not part of any ordered group
 * (i.e. order does not apply), otherwise a result carrying the element's own group index so
 * the caller can update its running maximum. When `elementName` belongs to multiple groups, the
 * lowest index still compatible with `maxSeenGroupIndex` is used, so it doesn't force the running
 * maximum ahead of what it actually needs to.
 */
export const validateElementOrder = (
    elementName: string,
    order: string[][] | undefined,
    maxSeenGroupIndex: number | undefined
): OrderValidationResult | undefined => {
    if (!order) return undefined;

    const groupIndices: number[] = getElementOrderGroupIndices(elementName, order);
    if (groupIndices.length === 0) return undefined;

    const eligibleIndices: number[] =
        maxSeenGroupIndex === undefined
            ? groupIndices
            : groupIndices.filter((index: number) => index >= maxSeenGroupIndex);

    if (eligibleIndices.length === 0) {
        return {
            isValid: false,
            groupIndex: Math.min(...groupIndices),
            message: `Element '<${elementName}>' is out of order. Expected order: ${order
                .map((group: string[]) => group.join('/'))
                .join(', then ')}.`
        };
    }

    return { isValid: true, groupIndex: Math.min(...eligibleIndices) };
};

/**
 * Parent element name whose allowed-child list should be used for validation/completion.
 * Uses instance `<children allow="..."/>` only under a component/widget tag in another file
 * (e.g. `<button>` in a screen). Editing the component's own XML uses `<view extends="...">` rules.
 */
export const resolveAllowedChildrenParentName = (
    schemaService: LVGLSchemaService,
    parentEntry: { name: string; extends?: string } | undefined,
    fileBaseName: string
): string => {
    const parentElement: string = parentEntry?.name || '';
    const defaultParent: string =
        parentEntry?.name === 'view' && parentEntry.extends ? parentEntry.extends : parentElement;

    const parentSchema: ElementSchema | undefined = parentElement ? schemaService.getElement(parentElement) : undefined;
    const usesChildrenAllowList: boolean =
        !!parentSchema?.filePath &&
        (parentSchema.type === 'component' || parentSchema.type === 'widget') &&
        Array.isArray(parentSchema.allowedChildren) &&
        fileBaseName !== parentElement;

    if (usesChildrenAllowList) {
        return parentElement;
    }

    return defaultParent;
};

export type ChildrenAllowTokenRange = {
    token: string;
    lineNumber: number;
    startColumn: number;
    endColumn: number;
};

/** Ranges of repeated tokens in a `<children allow="..."/>` attribute value (second occurrence onward). */
export const getDuplicateChildrenAllowTokenRanges = (allowProp: PropInfo): ChildrenAllowTokenRange[] => {
    const allowTokens: string[] = allowProp.propValue.trim().split(/\s+/).filter(Boolean);
    if (allowTokens.length < 2) return [];

    const quoteIndex: number = allowProp.fullProp.search(/["']/);
    if (quoteIndex === -1) return [];

    const valueBaseColumn: number = allowProp.startColumn + quoteIndex + 1;
    const seen: Set<string> = new Set();
    const duplicates: ChildrenAllowTokenRange[] = [];
    let searchPos = 0;
    const raw: string = allowProp.propValue;

    for (const token of allowTokens) {
        const tokenStart: number = raw.indexOf(token, searchPos);
        if (tokenStart === -1) continue;

        if (seen.has(token)) {
            duplicates.push({
                token,
                lineNumber: allowProp.lineNumber,
                startColumn: valueBaseColumn + tokenStart,
                endColumn: valueBaseColumn + tokenStart + token.length
            });
        } else {
            seen.add(token);
        }

        searchPos = tokenStart + token.length;
    }

    return duplicates;
};
